import React, { useState, useEffect } from 'react';
import { Layout } from '../../components/Layout';
import { adminApi } from '../../lib/api';
import api from '../../lib/api';
import { Card, CardContent, CardHeader, CardTitle } from '../../components/ui/card';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { Input } from '../../components/ui/input';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '../../components/ui/select';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from '../../components/ui/dropdown-menu';
import { formatDate } from '../../lib/utils';
import {
  Users,
  Search,
  MoreVertical,
  UserCheck,
  UserX,
  KeyRound,
  Loader2,
  CheckCircle,
  XCircle,
  Clock,
  Trash2,
  ChevronLeft,
  ChevronRight
} from 'lucide-react';
import { toast } from 'sonner';

const USERS_PER_PAGE = 20;

const AdminUsers = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchQuery, setSearchQuery] = useState('');
  const [roleFilter, setRoleFilter] = useState('all');
  const [statusFilter, setStatusFilter] = useState('all');
  const [approvalFilter, setApprovalFilter] = useState('all');
  const [currentPage, setCurrentPage] = useState(1);

  const fetchUsers = async () => {
    try {
      const params = { limit: 500 };
      if (roleFilter !== 'all') params.role = roleFilter;
      if (statusFilter !== 'all') params.is_active = statusFilter === 'active';
      
      const response = await adminApi.listUsers(params);
      setUsers(response.data);
    } catch (error) {
      console.error('Failed to fetch users:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchUsers();
  }, [roleFilter, statusFilter]);

  // Reset to page 1 when filters change
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, roleFilter, statusFilter, approvalFilter]);

  const handleToggleStatus = async (userId, currentStatus) => {
    setTimeout(async () => {
      try {
        await adminApi.updateUser(userId, { is_active: !currentStatus });
        toast.success(`User ${currentStatus ? 'deactivated' : 'activated'}`);
        fetchUsers();
      } catch (error) {
        toast.error('Failed to update user');
      }
    }, 200);
  };

  const handleApprove = async (userId, userName) => {
    try {
      await api.post(`/admin/users/${userId}/approve`);
      toast.success(`${userName} has been approved`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to approve user');
    }
  };

  const handleReject = async (userId, userName) => {
    if (!window.confirm(`Reject and remove ${userName}'s registration? This will delete their account.`)) return;
    try {
      await api.post(`/admin/users/${userId}/reject`);
      toast.success(`${userName} has been rejected`);
      fetchUsers();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to reject user');
    }
  };

  const handleDeleteUser = async (userId, userName) => {
    setTimeout(async () => {
      if (!window.confirm(`Delete ${userName} and all their data? This cannot be undone.`)) return;
      try {
        await api.delete(`/maintenance/user/${userId}`);
        toast.success(`${userName} deleted`);
        fetchUsers();
      } catch (error) {
        toast.error(error.response?.data?.detail || 'Failed to delete user');
      }
    }, 200);
  };

  const handleResetPassword = async (userId) => {
    setTimeout(async () => {
      if (window.confirm('Send a password reset email to this user?')) {
        try {
          await adminApi.resetUserPassword(userId);
          toast.success('Password reset email sent');
        } catch (error) {
          toast.error('Failed to reset password');
        }
      }
    }, 200);
  };

  // Filter users
  const filteredUsers = users.filter(user => {
    const matchesSearch = user.full_name.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.student_id.toLowerCase().includes(searchQuery.toLowerCase());
    
    if (approvalFilter === 'pending') {
      return matchesSearch && !user.is_approved && user.role !== 'admin';
    }
    if (approvalFilter === 'approved') {
      return matchesSearch && (user.is_approved || user.role === 'admin');
    }
    return matchesSearch;
  });

  // Pagination
  const totalPages = Math.ceil(filteredUsers.length / USERS_PER_PAGE);
  const startIdx = (currentPage - 1) * USERS_PER_PAGE;
  const paginatedUsers = filteredUsers.slice(startIdx, startIdx + USERS_PER_PAGE);
  const pendingCount = users.filter(u => !u.is_approved && u.role !== 'admin').length;

  const goToPage = (page) => {
    setCurrentPage(Math.max(1, Math.min(page, totalPages)));
  };

  // Generate page numbers to display
  const getPageNumbers = () => {
    const pages = [];
    const maxVisible = 5;
    let start = Math.max(1, currentPage - Math.floor(maxVisible / 2));
    let end = Math.min(totalPages, start + maxVisible - 1);
    if (end - start + 1 < maxVisible) {
      start = Math.max(1, end - maxVisible + 1);
    }
    for (let i = start; i <= end; i++) {
      pages.push(i);
    }
    return pages;
  };

  return (
    <Layout>
      <div className="space-y-6" data-testid="admin-users-page">
        {/* Header */}
        <div>
          <h1 className="font-serif text-3xl font-bold text-foreground">User Management</h1>
          <p className="text-muted-foreground mt-1">
            View, approve, and manage registered users
          </p>
        </div>

        {/* Pending Approval Alert */}
        {pendingCount > 0 && (
          <Card className="border-amber-200 bg-amber-50" data-testid="pending-alert">
            <CardContent className="p-4 flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="w-10 h-10 bg-amber-100 rounded-full flex items-center justify-center">
                  <Clock className="w-5 h-5 text-amber-600" />
                </div>
                <div>
                  <p className="font-semibold text-amber-900">{pendingCount} user{pendingCount !== 1 ? 's' : ''} pending approval</p>
                  <p className="text-sm text-amber-700">New registrations waiting for your review</p>
                </div>
              </div>
              <Button 
                variant="outline" 
                className="border-amber-300 text-amber-700 hover:bg-amber-100"
                onClick={() => setApprovalFilter('pending')}
                data-testid="show-pending-btn"
              >
                Show Pending
              </Button>
            </CardContent>
          </Card>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="p-4">
            <div className="flex flex-col sm:flex-row gap-4">
              <div className="flex-1 relative">
                <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4 text-muted-foreground" />
                <Input
                  placeholder="Search by name, email, or student ID..."
                  value={searchQuery}
                  onChange={(e) => setSearchQuery(e.target.value)}
                  className="pl-10 h-10"
                  data-testid="user-search"
                />
              </div>
              <Select value={approvalFilter} onValueChange={setApprovalFilter}>
                <SelectTrigger className="w-full sm:w-40 h-10" data-testid="approval-filter">
                  <SelectValue placeholder="Approval" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Users</SelectItem>
                  <SelectItem value="pending">Pending</SelectItem>
                  <SelectItem value="approved">Approved</SelectItem>
                </SelectContent>
              </Select>
              <Select value={roleFilter} onValueChange={setRoleFilter}>
                <SelectTrigger className="w-full sm:w-40 h-10" data-testid="role-filter">
                  <SelectValue placeholder="Role" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Roles</SelectItem>
                  <SelectItem value="student">Students</SelectItem>
                  <SelectItem value="admin">Admins</SelectItem>
                </SelectContent>
              </Select>
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full sm:w-40 h-10" data-testid="status-filter">
                  <SelectValue placeholder="Status" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">All Status</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="inactive">Inactive</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Users Table */}
        <Card>
          <CardHeader className="border-b border-border/40">
            <div className="flex items-center justify-between">
              <CardTitle className="font-serif">
                {filteredUsers.length} User{filteredUsers.length !== 1 ? 's' : ''}
                {approvalFilter === 'pending' && ' (Pending Approval)'}
              </CardTitle>
              {totalPages > 1 && (
                <p className="text-sm text-muted-foreground">
                  Showing {startIdx + 1}-{Math.min(startIdx + USERS_PER_PAGE, filteredUsers.length)} of {filteredUsers.length}
                </p>
              )}
            </div>
          </CardHeader>
          <CardContent className="p-0">
            {loading ? (
              <div className="flex items-center justify-center py-12">
                <Loader2 className="w-8 h-8 animate-spin text-muted-foreground" />
              </div>
            ) : filteredUsers.length === 0 ? (
              <div className="text-center py-12">
                <Users className="w-12 h-12 mx-auto text-muted-foreground/50 mb-4" />
                <p className="text-muted-foreground">No users found</p>
              </div>
            ) : (
              <>
                <div className="overflow-x-auto">
                  <table className="data-table">
                    <thead>
                      <tr>
                        <th>User</th>
                        <th>Student ID</th>
                        <th>Role</th>
                        <th>Approval</th>
                        <th>Status</th>
                        <th>Auth</th>
                        <th>Joined</th>
                        <th></th>
                      </tr>
                    </thead>
                    <tbody>
                      {paginatedUsers.map((user) => {
                        const isPending = !user.is_approved && user.role !== 'admin';
                        return (
                          <tr key={user.id} className={isPending ? 'bg-amber-50/50' : ''} data-testid={`user-row-${user.id}`}>
                            <td>
                              <div>
                                <p className="font-medium text-foreground">{user.full_name}</p>
                                <p className="text-sm text-muted-foreground">{user.email}</p>
                              </div>
                            </td>
                            <td>
                              <span className="font-mono text-sm">{user.student_id}</span>
                            </td>
                            <td>
                              <Badge variant={user.role === 'admin' ? 'default' : 'secondary'}>
                                {user.role}
                              </Badge>
                            </td>
                            <td>
                              {user.role === 'admin' ? (
                                <Badge className="bg-emerald-100 text-emerald-700" variant="secondary">Admin</Badge>
                              ) : user.is_approved ? (
                                <Badge className="bg-emerald-100 text-emerald-700" variant="secondary">
                                  <CheckCircle className="w-3 h-3 mr-1" />
                                  Approved
                                </Badge>
                              ) : (
                                <Badge className="bg-amber-100 text-amber-700" variant="secondary">
                                  <Clock className="w-3 h-3 mr-1" />
                                  Pending
                                </Badge>
                              )}
                            </td>
                            <td>
                              <Badge 
                                variant="secondary"
                                className={user.is_active ? 'bg-emerald-100 text-emerald-700' : 'bg-red-100 text-red-700'}
                              >
                                {user.is_active ? 'Active' : 'Inactive'}
                              </Badge>
                            </td>
                            <td>
                              <span className="text-xs text-muted-foreground capitalize">{user.auth_provider || 'email'}</span>
                            </td>
                            <td>
                              <span className="text-sm text-muted-foreground">
                                {formatDate(user.created_at)}
                              </span>
                            </td>
                            <td>
                              <div className="flex items-center gap-1">
                                {isPending && (
                                  <>
                                    <Button
                                      size="sm"
                                      className="bg-emerald-600 hover:bg-emerald-700 text-white h-8 px-3"
                                      onClick={() => handleApprove(user.id, user.full_name)}
                                      data-testid={`approve-user-${user.id}`}
                                    >
                                      <UserCheck className="w-3.5 h-3.5 mr-1" />
                                      Approve
                                    </Button>
                                    <Button
                                      size="sm"
                                      variant="destructive"
                                      className="h-8 px-3"
                                      onClick={() => handleReject(user.id, user.full_name)}
                                      data-testid={`reject-user-${user.id}`}
                                    >
                                      <XCircle className="w-3.5 h-3.5 mr-1" />
                                      Reject
                                    </Button>
                                  </>
                                )}
                                <DropdownMenu>
                                  <DropdownMenuTrigger asChild>
                                    <Button variant="ghost" size="sm" data-testid={`user-menu-${user.id}`}>
                                      <MoreVertical className="w-4 h-4" />
                                    </Button>
                                  </DropdownMenuTrigger>
                                  <DropdownMenuContent align="end">
                                    <DropdownMenuItem
                                      onClick={() => handleToggleStatus(user.id, user.is_active)}
                                      data-testid={`toggle-status-${user.id}`}
                                    >
                                      {user.is_active ? (
                                        <>
                                          <UserX className="w-4 h-4 mr-2" />
                                          Deactivate
                                        </>
                                      ) : (
                                        <>
                                          <UserCheck className="w-4 h-4 mr-2" />
                                          Activate
                                        </>
                                      )}
                                    </DropdownMenuItem>
                                    <DropdownMenuItem
                                      onClick={() => handleResetPassword(user.id)}
                                      data-testid={`reset-password-${user.id}`}
                                    >
                                      <KeyRound className="w-4 h-4 mr-2" />
                                      Reset Password
                                    </DropdownMenuItem>
                                    {user.role !== 'admin' && (
                                      <DropdownMenuItem
                                        className="text-red-600"
                                        onClick={() => handleDeleteUser(user.id, user.full_name)}
                                        data-testid={`delete-user-${user.id}`}
                                      >
                                        <Trash2 className="w-4 h-4 mr-2" />
                                        Delete User
                                      </DropdownMenuItem>
                                    )}
                                  </DropdownMenuContent>
                                </DropdownMenu>
                              </div>
                            </td>
                          </tr>
                        );
                      })}
                    </tbody>
                  </table>
                </div>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between px-6 py-4 border-t border-border/40" data-testid="pagination">
                    <p className="text-sm text-muted-foreground">
                      Page {currentPage} of {totalPages}
                    </p>
                    <div className="flex items-center gap-1">
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => goToPage(currentPage - 1)}
                        disabled={currentPage === 1}
                        data-testid="prev-page-btn"
                      >
                        <ChevronLeft className="w-4 h-4" />
                      </Button>
                      {getPageNumbers().map((page) => (
                        <Button
                          key={page}
                          variant={page === currentPage ? 'default' : 'outline'}
                          size="sm"
                          className="w-9"
                          onClick={() => goToPage(page)}
                          data-testid={`page-${page}-btn`}
                        >
                          {page}
                        </Button>
                      ))}
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => goToPage(currentPage + 1)}
                        disabled={currentPage === totalPages}
                        data-testid="next-page-btn"
                      >
                        <ChevronRight className="w-4 h-4" />
                      </Button>
                    </div>
                  </div>
                )}
              </>
            )}
          </CardContent>
        </Card>
      </div>
    </Layout>
  );
};

export default AdminUsers;
